import type { NotificationPreferences, Task } from '@/api/types';
import { ACCOUNT_PARAM } from '@/app/accountSearchParam';
import { addDays, formatDueTime } from '@/features/tasks/due';
import { dateLocale, todayCalendarDay } from '@/utils/format';
import { inQuietHours, SCHEDULE_HORIZON_MS, STALE_AFTER_MS } from './calendarReminders';

/**
 * Task reminders, the pure half: which reminders are due when, what their
 * notice says, and where its click goes. The hook that turns this into timers
 * and notices is `useTaskReminders`.
 *
 * Two parties raise these notices, as for calendar reminders
 * (calendarReminders.ts). The server pushes each one at its `remindAt` to every
 * push subscription of the owner — it needs no stored password, because it
 * computed `remindAt` itself and reads nothing from the mail server — and the
 * open app raises its own as a fallback for a browser the push cannot reach:
 * one that never registered, and the desktop app (no Web Push). Both must name
 * the same reminder the same way, which is what `taskReminderTag` and
 * `taskReminderUrl` are for: a change to either is a change to the Go side too.
 *
 * The staleness rule, `STALE_AFTER_MS`, and the scheduling horizon are the
 * calendar's, shared on purpose (see `planTaskReminders`).
 */

/** Deep-link parameters the Tasks screen reads (routes/tasks/TasksView.tsx). */
export const TASK_LIST_PARAM = 'list';
export const TASK_ID_PARAM = 'task';

/**
 * The notification tag for one reminder of one task.
 *
 * `remindAt` is part of it so a task whose reminder was moved (a new due
 * date, another choice) is a new notice rather than one the tray already
 * holds. MUST match the server's tag for the task reminder push
 * (`ols-task-reminder-<task id>-<remind_at unix seconds>`), or the push and the
 * open app's fallback raise two notices for one reminder.
 */
export function taskReminderTag(taskId: string, remindAt: string): string {
  return `ols-task-reminder-${taskId}-${Math.floor(Date.parse(remindAt) / 1000)}`;
}

/**
 * Go's `url.QueryEscape`, which the server builds its URL with: a space is
 * `+`, and `!'()*` are escaped where `encodeURIComponent` leaves them. Both
 * decode to the same value (URLSearchParams reads `+` as a space); matching
 * the bytes too keeps the two sides' links identical, as the contract asks.
 */
function queryEscape(value: string): string {
  return encodeURIComponent(value)
    .replace(/%20/g, '+')
    .replace(/[!'()*]/g, (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`);
}

/**
 * Where a reminder's click lands: the task's list, with that task open.
 * `?account=` comes last and is consumed first (app/accountSearchParam.ts),
 * because list and task ids only mean something inside one mailbox; the Tasks
 * screen reads `list` and `task` once it is gone. Order and escaping are the
 * server's (`taskReminderURL`), so the two sides build byte-identical URLs.
 */
export function taskReminderUrl(accountId: string, listId: string, taskId: string): string {
  return (
    `/tasks?${TASK_LIST_PARAM}=${queryEscape(listId)}` +
    `&${TASK_ID_PARAM}=${queryEscape(taskId)}` +
    `&${ACCOUNT_PARAM}=${queryEscape(accountId)}`
  );
}

/** "Thu, Sep 24" in the chosen locale: a wall-clock day, whatever the zone. */
function formatDueDay(day: string): string {
  // Noon UTC formatted in UTC is the day itself (features/tasks/due.ts).
  return new Intl.DateTimeFormat(dateLocale(), {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(new Date(`${day}T12:00:00Z`));
}

/**
 * "Due at 2:30 PM", "Due today", "Due tomorrow at 9:00 AM", "Due Thu, Sep 24"
 * — the server's wording, read in the owner's chosen zone and locale.
 *
 * A due date is a wall-clock day with no zone (like Google Tasks), so it is
 * compared as a day against today in the zone Settings → General chose
 * (`todayCalendarDay`), and its time is printed as written, never converted.
 * The server's " · <mailbox>" suffix for a connected mailbox is left out, as
 * the calendar's in-app reminder leaves it out: it names the mailbox for a
 * lock screen that does not know which one this is; the open app does.
 */
export function taskReminderBody(task: Pick<Task, 'dueDate' | 'dueTime'>, now: Date): string {
  if (!task.dueDate) return '';
  const today = todayCalendarDay(now);
  const time = task.dueTime ? formatDueTime(task.dueTime) : '';
  if (task.dueDate === today) return time ? `Due at ${time}` : 'Due today';
  if (task.dueDate === addDays(today, 1)) return time ? `Due tomorrow at ${time}` : 'Due tomorrow';
  const day = formatDueDay(task.dueDate);
  return time ? `Due ${day} at ${time}` : `Due ${day}`;
}

export interface PlannedTaskReminder {
  /** The tag: one task has one reminder at a time. */
  key: string;
  tag: string;
  triggerAt: number;
  title: string;
  task: Task;
}

/**
 * Every task reminder that should be raised from now on, soonest first.
 *
 * The input is the server's `view=reminders` read, which already holds only
 * open tasks (a subtask of a completed parent counts as done) whose reminder
 * is still worth sending. It is filtered again here because the cache it comes
 * from is patched optimistically by the Tasks screen: a task completed a
 * moment ago is still in the list, marked complete, until the refetch.
 *
 * **Stale after fifteen minutes past `remindAt`.** The server keeps a
 * reminder sendable until `max(due, remindAt + 15 min)` for a timed task and
 * until the end of the due day for a date-only one (its `remind_until`). The
 * in-app fallback keeps the calendar's shorter rule instead: a tab opened a
 * few minutes late still says the task is due, one opened hours after "1 day
 * before" does not dig it up. Working out the due instant here would need the
 * zone the server chose (Settings → General, else quiet hours, else UTC),
 * which this side can only guess; the fifteen minutes need no guess, and they
 * never outlast the server's window, so the fallback never raises a reminder
 * the server itself would call over.
 */
export function planTaskReminders(tasks: readonly Task[], now: number): PlannedTaskReminder[] {
  const out: PlannedTaskReminder[] = [];
  const seen = new Set<string>();
  for (const task of tasks) {
    if (task.completedAt !== null || !task.remindAt) continue;
    const triggerAt = Date.parse(task.remindAt);
    if (!Number.isFinite(triggerAt)) continue;
    if (triggerAt > now + SCHEDULE_HORIZON_MS) continue;
    if (triggerAt < now - STALE_AFTER_MS) continue;
    const tag = taskReminderTag(task.id, task.remindAt);
    if (seen.has(tag)) continue;
    seen.add(tag);
    out.push({ key: tag, tag, triggerAt, title: task.title.trim() || '(No title)', task });
  }
  return out.sort((a, b) => a.triggerAt - b.triggerAt);
}

/** Whether a planned reminder is past the point of being raised (see above). */
export function taskReminderStale(item: PlannedTaskReminder, now: number): boolean {
  return now > item.triggerAt + STALE_AFTER_MS;
}

/**
 * The owner's preferences allow a task reminder right now: notifications on,
 * task reminders on, no snooze running and not inside quiet hours — the same
 * checks as `remindersAllowed`, with this switch in place of the calendar's.
 */
export function taskRemindersAllowed(
  prefs: NotificationPreferences | undefined,
  now: Date,
  zone: string,
): boolean {
  if (!prefs || !prefs.enabled || !prefs.taskReminders) return false;
  if (prefs.snoozedUntil) {
    const until = Date.parse(prefs.snoozedUntil);
    if (Number.isFinite(until) && until > now.getTime()) return false;
  }
  return !inQuietHours(prefs.quietHours, now, zone);
}
